/* eslint @typescript-eslint/no-unused-vars: */

import {
    Alert,
    Button,
    CircularProgress,
    Grid,
    Typography,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useCookies } from "react-cookie"
import { Trans, useTranslation } from "react-i18next"
import "./MembershipPaymentPage.scss"
import {
    MembershipPaymentRoute,
    MembershipPaymentRouteProps,
} from "../MembershipPaymentFlow/MembershipPaymentFlow"
import {
    getMembershipDetails,
    MembershipDetail,
    PaymentInstructionCode,
} from "../../../services/ntuc/memberService/membershipDetails"
import {
    DialogButtonAction,
    useAlertErrorHandler,
    useDialogErrorHandler,
} from "../../../hooks/ErrorHandler"
import { AppCookies } from "../../../helpers/cookies"
import { ErrorCode } from "../../../services/ntuc/applicationError"
import { useMembershipStore } from "../../../store/membership/MembershipSignupStore"
import { MembershipActionType } from "../../../store/membership/MembershipReducer"
import ErrorDialog from "../../../components/Dialog/ErrorDialog"

const MembershipPaymentPage = (props: MembershipPaymentRouteProps) => {
    const { onNavigateTo } = props

    const [, setCookie] = useCookies()
    const { t } = useTranslation(`membership-payment`)
    const [membershipDetail, setMembershipDetail] = useState<
    MembershipDetail | undefined
    >(undefined)
    const [isRetrievingMembership, setIsRetrievingMembership] =
    useState<boolean>(true)
    const [alert, alertErrorHandler] = useAlertErrorHandler()
    const [upfrontPaymentAlert, upfrontPaymentAlertHandler] =
    useAlertErrorHandler()
    const [hasOutstandingUpfrontPayment, setHasOutstandingUpfrontPayment] =
    useState<boolean>(false)
    const [membershipState, dispatchMembershipAction] = useMembershipStore()
    const [dialog, dialogErrorHandler, closeHandler] = useDialogErrorHandler({
        content: ``,
        title: ``,
        display: false,
    })

    useEffect(() => {
        if (!window.myNtucConfig.authToken) {
            console.error(`Auth token not found.`)
            dialogErrorHandler({
                errorCode: ErrorCode.UNAUTHENTICATION_REQUEST,
            })
        }
    }, [])

    useEffect(() => {
        const upfrontPaymentError =
      membershipState.hadErrorSettingUpUpfrontPaymentRetry
          ? {
              errorCode: ErrorCode.RETRY_UPFRONT_PAYMENT_FAILED,
          }
          : undefined
        upfrontPaymentAlertHandler(upfrontPaymentError)
    }, [membershipState])

    useEffect(() => {
        if (window.myNtucConfig.membershipId) {
            setCookie(AppCookies.MEMBERSHIP_ID, window.myNtucConfig.membershipId)
            sessionStorage.setItem(AppCookies.MEMBERSHIP_ID, window.myNtucConfig.membershipId)
        }
    }, [setCookie])

    useEffect(() => {
        getMembershipDetails()
            .then((response) => response.content.memberships)
            .then(findMembershipDetail)
            .then(setMembershipDetail)
            .catch(alertErrorHandler)
    }, [])

    useEffect(() => {
        if (membershipDetail?.outstandingPaymentId) {
            setHasOutstandingUpfrontPayment(true)
        }
    }, [membershipDetail])

    const findMembershipDetail = (
        memberships: MembershipDetail[],
    ): MembershipDetail | undefined => {
        if (memberships.length == 0) {
            console.error(`No membership was found.`)
            alertErrorHandler({
                error: `Membership not found`,
                errorDescription: `No membership was found.`,
                errorCode: ErrorCode.INACTIVE_MEMBERSHIP,
            })
            return
        }

        const membership = memberships.find((obj) => {
            console.log(`find membership by id.`)
            return `${obj.id}` == window.myNtucConfig.membershipId
        })

        if (process.env.REACT_APP_NTUC_TARGET == `demo` && membership == null) {
            const membershipIds: number[] = memberships.map((item) => item.id)
            window.alert(
                `DEMO ONLY\nMembership with ID ${window.myNtucConfig.membershipId} does not belong to logged in user.\nCurrent user has membership(s) with ID(s) ${membershipIds}`,
            )
        }

        if (!membership) {
            console.error(`The membership ID provided does not belong to this user`)
            alertErrorHandler({
                error: `Wrong membership ID`,
                errorDescription: `The membership ID provided does not belong to this user`,
                errorCode: ErrorCode.UNKNOWN_ERROR,
            })
            return
        }

        if (window.myNtucConfig.renewalCampaignCode) {
            props.onNavigateTo(MembershipPaymentRoute.MembershipFeePayment)
        }

        setIsRetrievingMembership(false)

        dispatchMembershipAction({
            type: MembershipActionType.ADD_MEMBERSHIP_DETAILS,
            value: {
                membership,
            },
        })

        return membership
    }

    const handleNavigateTo = () => {
        onNavigateTo(MembershipPaymentRoute.Recurring)
    }

    const handleNavigateToRetryUpfrontPayment = () => {
        onNavigateTo(MembershipPaymentRoute.RetryUpfrontPayment)
    }

    const handleNavigateToArrear = () => {
        onNavigateTo(MembershipPaymentRoute.MembershipFeePayment)
    }

    const MembershipDescription = () => {
        return (
            <Grid item xs={12} md={11}>
                <Typography textAlign="left">{t(`content.title`)}</Typography>
                <ul>
                    <Typography>
                        <Trans>{t(`content.description`)}</Trans>
                    </Typography>
                </ul>
            </Grid>
        )
    }

    const ErrorToast = () => {
        if (alert && dialog.content !== `errors.user_login`) {
            return (
                <Grid item container xs={12} justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <Alert severity="error">{t(alert.content, { ns: `common` })}</Alert>
                    </Grid>
                </Grid>
            )
        }
        return <div></div>
    }

    const RecurringPaymentButton = () => {
        return (
            <Grid item container xs={12} justifyContent="center">
                <Grid item hidden={!showRecurringButton()} xs={12} md={4}>
                    <Button
                        className="navigation-button"
                        onClick={handleNavigateTo}
                        size="large"
                        variant="contained"
                        fullWidth
                    >
                        {t(`buttons.set_recurring`)}
                    </Button>
                </Grid>
            </Grid>
        )
    }

    const MembershipPaymentButton = () => {
        return (
            <Grid item container xs={12} justifyContent="center">
                <Grid item hidden={!membershipDetail?.showPaymentOption} xs={12} md={4}>
                    <Button
                        className="navigation-button"
                        onClick={handleNavigateToArrear}
                        size="large"
                        variant="contained"
                        fullWidth
                    >
                        {t(`buttons.arrears`)}
                    </Button>
                </Grid>
            </Grid>
        )
    }

    const RetryUpfrontPaymentButton = () => {
        return (
            <Grid item container xs={12} justifyContent="center">
                <Grid item hidden={!membershipDetail?.showPaymentOption} xs={12} md={4}>
                    <Button
                        className="navigation-button"
                        onClick={handleNavigateToRetryUpfrontPayment}
                        size="large"
                        variant="contained"
                        fullWidth
                    >
                        {t(`buttons.retry_upfront_payment`)}
                    </Button>
                </Grid>
            </Grid>
        )
    }

    const NoOutstandingFeesMessage = () => {
        if (alert) {
            return (
                <Grid
                    item
                    container
                    hidden={membershipDetail?.showPaymentOption || alert.display}
                    textAlign="center"
                    xs={12}
                    justifyContent="center"
                >
                    <Grid
                        item
                        hidden={membershipDetail?.showPaymentOption || alert.display}
                        xs={8}
                    >
                        {t(`no-outstanding-fees-message`)}
                    </Grid>
                </Grid>
            )
        }
        return <div></div>
    }

    const LoadingProgress = () => {
        return (
            <Grid container item justifyContent="center" py={6} xs={12}>
                <CircularProgress />
            </Grid>
        )
    }

    const UpfrontPaymentAlert = () => {
        return (
            <Grid item container xs={12} justifyContent="center">
                <Grid item xs={12} md={6}>
                    {upfrontPaymentAlert && upfrontPaymentAlert.display && (
                        <Alert severity="error">
                            {t(upfrontPaymentAlert.content, { ns: `common` })}
                        </Alert>
                    )}
                </Grid>
            </Grid>
        )
    }

    const showLoading = () => {
        return isRetrievingMembership && (alert == undefined || !alert.display)
    }

    const showRecurringButton = () => {
        return (
            membershipDetail?.paymentInstructionCode ==
          PaymentInstructionCode.OneTime ||
            membershipDetail?.paymentInstructionCode ==
          PaymentInstructionCode.Giro ||
            membershipDetail?.paymentInstructionCode ==
          PaymentInstructionCode.CreditCardRecurring ||
            membershipDetail?.paymentInstructionCode == PaymentInstructionCode.Null
        )
    }

    const PageButtons = () => {
        return (
            <Grid
                item
                container
                xs={12}
                direction="column"
                alignItems="center"
                rowSpacing={2}
            >
                {alert && alert.display && <ErrorToast />}
                {showLoading() && <LoadingProgress />}
                {!showLoading() && !hasOutstandingUpfrontPayment && (
                    <MembershipPaymentButton />
                )}
                {!showLoading() && hasOutstandingUpfrontPayment && (
                    <RetryUpfrontPaymentButton />
                )}
                {!showLoading() && <RecurringPaymentButton />}
                {!showLoading() && <NoOutstandingFeesMessage />}
            </Grid>
        )
    }

    const handleNavigateToLogin = () => {
        console.log(`handleNavigateToLogin: `, window.myNtucConfig.loginUrl)
        if (window.myNtucConfig.loginUrl) {
            window.location.replace(window.myNtucConfig.loginUrl)
        }
    }

    const handleDialogClick = (action?: string): void => {
        switch (action) {
            case DialogButtonAction.LOGIN_SINGPASS:
                console.log(`Login Navigated.`)
                return handleNavigateToLogin()
            case DialogButtonAction.CANCEL:
                console.log(`Cancelled.`)
                return closeHandler()
            default:
                break
        }
    }

    return (
        <Grid
            container
            justifyContent="center"
            direction="column"
            rowSpacing={3}
            padding={2}
        >
            <ErrorDialog
                open={dialog.display}
                content={t(dialog.content, { ns: `common` })}
                title={t(dialog.title, { ns: `common` })}
                onClose={closeHandler}
            >
                {dialog.primaryButton && (
                    <Grid item sm={6} xs={12}>
                        <Button
                            fullWidth
                            onClick={() => handleDialogClick(dialog.primaryButton)}
                            variant="contained"
                        >
                            {t(dialog.primaryButton, { ns: `common` })}
                        </Button>
                    </Grid>
                )}
                {dialog.secondaryButton && (
                    <Grid item sm={6} xs={12}>
                        {` `}
                        <Button
                            fullWidth
                            onClick={() => handleDialogClick(dialog.secondaryButton)}
                        >
                            {t(dialog.secondaryButton, { ns: `common` })}
                        </Button>
                    </Grid>
                )}
            </ErrorDialog>
            <Grid item xs={12} md={11} mb={4}>
                <Typography textAlign="center" variant="h2">
                    {t(`title`)}
                </Typography>
            </Grid>
            <Grid className="center-on-mobile" item xs={12} md={11}>
                <Typography textAlign="left">{t(`subheader`)}</Typography>
            </Grid>
            <MembershipDescription />
            <UpfrontPaymentAlert />
            <PageButtons />
            <Grid item xs={12} md={11} m={{ xs: 0, md: 5 }} textAlign="center">
                <Alert severity="info">
                    <Typography textAlign="left">{t(`footnote`)}</Typography>
                </Alert>
            </Grid>
        </Grid>
    )
}

export default MembershipPaymentPage
